(() => {
  if (window.cartAcknowledgementController) {
    window.cartAcknowledgementController.refresh();
    return;
  }

  const componentSelector = '[data-cart-acknowledgement]';
  const inputSelector = '[data-cart-acknowledgement-input]';
  const checkoutSelector = '[name="checkout"]';
  const acceleratedSelector = '[data-cart-accelerated-checkout]';
  let requestSequence = 0;

  const getScope = (component) => component.closest('[data-cart-checkout-scope]');

  const getComplianceError = (input) => {
    const describedBy = input.getAttribute('aria-describedby');
    if (describedBy) {
      const error = document.getElementById(describedBy.split(/\s+/)[0]);
      if (error) return error;
    }
    const field = input.closest('.compliance-checkbox');
    const sibling = field?.nextElementSibling;
    return sibling?.classList.contains('compliance-checkbox__error') ? sibling : null;
  };

  const setComplianceValidity = (input, valid) => {
    const error = getComplianceError(input);
    input.classList.toggle('input--error', !valid);
    if (valid) {
      input.removeAttribute('aria-invalid');
      if (error) error.classList.add('hide');
    } else {
      input.setAttribute('aria-invalid', 'true');
      if (error) error.classList.remove('hide');
    }
  };

  const setError = (component, message = '') => {
    const input = component.querySelector(inputSelector);
    const error = component.querySelector('[data-cart-acknowledgement-error]');
    if (!input || !error) return;

    const messageElement = error.querySelector('span:not(.visually-hidden)');
    if (message && messageElement) messageElement.textContent = message;
    error.classList.toggle('hide', !message);
    input.classList.toggle('input--error', Boolean(message));
    if (message) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }
  };

  const setScopeState = (component, state) => {
    const scope = getScope(component);
    if (!scope) return;

    const accepted = state === 'true';
    scope.dataset.cartAccepted = state;

    scope.querySelectorAll(checkoutSelector).forEach((button) => {
      if (!accepted) {
        if (!button.disabled) button.dataset.cartAcknowledgementDisabled = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.cartAcknowledgementDisabled === 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        delete button.dataset.cartAcknowledgementDisabled;
      }
    });

    scope.querySelectorAll(acceleratedSelector).forEach((container) => {
      if (accepted) {
        container.removeAttribute('inert');
        container.removeAttribute('aria-disabled');
      } else {
        container.setAttribute('inert', '');
        container.setAttribute('aria-disabled', 'true');
      }
    });
  };

  const initializeComponent = (component) => {
    const input = component.querySelector(inputSelector);
    if (!input) return;

    if (component.dataset.cartAccepted === 'pending') return;
    if (component.dataset.cartAcknowledgementInitialized !== 'true') {
      component.dataset.cartAccepted = input.checked ? 'true' : 'false';
      component.dataset.cartAcknowledgementInitialized = 'true';
      input.disabled = false;
    }
    setScopeState(component, component.dataset.cartAccepted);
  };

  const refresh = () => {
    document.querySelectorAll(componentSelector).forEach(initializeComponent);
  };

  const findComponentForForm = (form) => {
    if (!form || !form.id) return null;
    return Array.from(document.querySelectorAll(componentSelector)).find(
      (component) => component.dataset.cartFormId === form.id
    );
  };

  const rejectCheckout = (component) => {
    if (!component) return false;
    const input = component.querySelector(inputSelector);
    if (input && input.checked && component.dataset.cartAccepted === 'true') return false;

    setError(component, component.dataset.requiredError);
    if (input) input.focus();
    return true;
  };

  document.addEventListener(
    'invalid',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const input = event.target.closest('.compliance-checkbox__input');
      if (input) setComplianceValidity(input, false);
    },
    true
  );

  document.addEventListener('change', async (event) => {
    if (!(event.target instanceof Element)) return;
    const complianceInput = event.target.closest('.compliance-checkbox__input');
    if (complianceInput && complianceInput.checked) {
      setComplianceValidity(complianceInput, true);
    }

    const input = event.target.closest(inputSelector);
    if (!input) return;

    const component = input.closest(componentSelector);
    if (!component) return;

    const accepted = input.checked;
    const currentRequest = ++requestSequence;
    const updateUrl = component.dataset.cartUpdateUrl;
    const previousState = component.dataset.cartAccepted === 'true';
    const components = Array.from(document.querySelectorAll(componentSelector));

    setError(component);
    components.forEach((item) => {
      const itemInput = item.querySelector(inputSelector);
      if (itemInput) itemInput.disabled = true;
      item.dataset.cartAccepted = 'pending';
      setScopeState(item, 'pending');
    });

    try {
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            legal_acknowledgement: accepted ? 'accepted' : ''
          }
        })
      });

      if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);
      await response.json();
      if (currentRequest !== requestSequence) return;

      const liveComponents = Array.from(document.querySelectorAll(componentSelector));
      liveComponents.forEach((item) => {
        const itemInput = item.querySelector(inputSelector);
        if (itemInput) {
          itemInput.checked = accepted;
          itemInput.disabled = false;
        }
        item.dataset.cartAccepted = accepted ? 'true' : 'false';
        setError(item);
        setScopeState(item, item.dataset.cartAccepted);
      });
    } catch (error) {
      if (currentRequest !== requestSequence) return;
      const liveComponents = Array.from(document.querySelectorAll(componentSelector));
      liveComponents.forEach((item) => {
        const itemInput = item.querySelector(inputSelector);
        if (itemInput) {
          itemInput.checked = previousState;
          itemInput.disabled = false;
        }
        item.dataset.cartAccepted = previousState ? 'true' : 'false';
        setScopeState(item, item.dataset.cartAccepted);
      });
      setError(component, component.dataset.saveError);
      console.error(error);
    }
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const checkout = event.target.closest(`${checkoutSelector}, ${acceleratedSelector}`);
      if (!checkout) return;
      const scope = checkout.closest('[data-cart-checkout-scope]');
      const component = scope ? scope.querySelector(componentSelector) : null;
      if (!rejectCheckout(component)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const component = findComponentForForm(event.target);
      if (!component) return;

      const isCheckoutSubmission = !event.submitter || event.submitter.matches(checkoutSelector);
      if (!isCheckoutSubmission || !rejectCheckout(component)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  const observer = new MutationObserver((records) => {
    const needsRefresh = records.some((record) =>
      Array.from(record.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.(`${componentSelector}, ${checkoutSelector}, ${acceleratedSelector}`) ||
            node.querySelector?.(`${componentSelector}, ${checkoutSelector}, ${acceleratedSelector}`))
      )
    );
    if (needsRefresh) refresh();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.cartAcknowledgementController = { refresh };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
})();

